<script lang="ts">
import type { Cell, CellContext, Header, RowData, TableMeta } from '@tanstack/table-core'
import type {
  ColumnDef,
  ColumnFiltersOptions,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningOptions,
  ColumnPinningState,
  ColumnSizingInfoState,
  ColumnSizingOptions,
  ColumnSizingState,
  CoreOptions,
  ExpandedOptions,
  ExpandedState,
  FacetedOptions,
  GlobalFilterOptions,
  GroupingOptions,
  GroupingState,
  PaginationOptions,
  PaginationState,
  Row,
  RowPinningOptions,
  RowPinningState,
  RowSelectionOptions,
  RowSelectionState,
  SortingOptions,
  SortingState,
  Updater,
  VisibilityOptions,
  VisibilityState,
} from '@tanstack/vue-table'
import type {
  HoveringOptions,
  HoveringRowAPI,
  HoveringTableAPI,
  HoveringTableState,
} from 'tanstack-table-hovering'
import type { Component, Ref, WatchOptions } from 'vue'

declare module '@tanstack/table-core' {
  interface ColumnMeta<TData extends RowData, TValue> {
    class?: {
      th?: string | ((cell: Header<TData, TValue>) => string)
      td?: string | ((cell: Cell<TData, TValue>) => string)
    }
  }
  interface TableMeta<TData> {
    class?: {
      tr?: string | ((row: Row<TData>) => string)
    }
  }
  interface TableState extends HoveringTableState {}
  interface Table<TData extends RowData> extends HoveringTableAPI<TData> {}
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface TableOptionsResolved<TData extends RowData> extends HoveringOptions {}
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface Row<TData extends RowData> extends HoveringRowAPI {}
}

export type TableRow<T> = Row<T>
export type TableData = RowData
export type TableColumn<T extends TableData, D = unknown> = ColumnDef<T, D> & {
  isSortable?: boolean
  labelIcon?: string
  hideHeaderText?: boolean
  tooltip?: string
}

export interface TableOptions<T extends TableData> extends Omit<CoreOptions<T>, 'data' | 'columns' | 'getCoreRowModel' | 'state' | 'onStateChange' | 'renderFallbackValue'> {
  state?: CoreOptions<T>['state']
  onStateChange?: CoreOptions<T>['onStateChange']
  renderFallbackValue?: CoreOptions<T>['renderFallbackValue']
}

export interface TableProps<T extends TableData> extends TableOptions<T> {
  as?: string | Component
  data?: T[]
  columns?: TableColumn<T>[]
  caption?: string
  meta?: TableMeta<T>
  empty?: string
  sticky?: boolean
  loading?: boolean
  hideSelectAllCheckbox?: boolean
  watchOptions?: WatchOptions
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/global-filtering#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/global-filtering)
   */
  globalFilterOptions?: Omit<GlobalFilterOptions<T>, 'onGlobalFilterChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/column-filtering#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/column-filtering)
   */
  columnFiltersOptions?: Omit<ColumnFiltersOptions<T>, 'getFilteredRowModel' | 'onColumnFiltersChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/column-pinning#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/column-pinning)
   */
  columnPinningOptions?: Omit<ColumnPinningOptions, 'onColumnPinningChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/column-sizing#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/column-sizing)
   */
  columnSizingOptions?: Omit<ColumnSizingOptions, 'onColumnSizingChange' | 'onColumnSizingInfoChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/column-visibility#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/column-visibility)
   */
  visibilityOptions?: Omit<VisibilityOptions, 'onColumnVisibilityChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/sorting#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/sorting)
   */
  sortingOptions?: Omit<SortingOptions<T>, 'getSortedRowModel' | 'onSortingChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/grouping#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/grouping)
   */
  groupingOptions?: Omit<GroupingOptions, 'onGroupingChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/expanding#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/expanding)
   */
  expandedOptions?: Omit<ExpandedOptions<T>, 'getExpandedRowModel' | 'onExpandedChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/row-selection#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/row-selection)
   */
  rowSelectionOptions?: Omit<RowSelectionOptions<T>, 'onRowSelectionChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/row-pinning#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/row-pinning)
   */
  rowPinningOptions?: Omit<RowPinningOptions<T>, 'onRowPinningChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/pagination#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/pagination)
   */
  paginationOptions?: Omit<PaginationOptions, 'onPaginationChange'>
  /**
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/features/column-faceting#table-options)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/column-faceting)
   */
  facetedOptions?: FacetedOptions<T>
  onSelect?: (row: TableRow<T>, e?: Event) => void
  isSelectable?: boolean
  class?: string | object
}

export interface TableSlots<T> {
  expanded?: (props: { row: Row<T> }) => any
  empty?: () => any
  loading?: () => any
  caption?: () => any
  actions?: (props: CellContext<T, unknown>) => any
  primaryAction?: (props: CellContext<T, unknown>) => any
  [key: string]: ((props: any) => any) | undefined
}

const ui = {
  root: 'relative overflow-auto',
  base: 'min-w-full overflow-clip',
  caption: 'sr-only',
  thead: 'relative after:absolute after:content-[""] after:inset-x-0 after:bottom-0 after:h-px after:bg-secondary-300',
  tbody: 'divide-y divide-secondary-300 border-b border-secondary-300',
  tr: 'data-[selected=true]:bg-elevated/50 data-[disabled=true]:opacity-30 data-[disabled=true]:cursor-default',
  th: {
    base: 'px-4 py-2 text-sm text-left text-secondary-700 font-semibold [&:has([role=checkbox])]:pe-0',
    pinned: 'sticky bg-default/75 data-[pinned=left]:left-0 data-[pinned=right]:right-0',
  },
  td: {
    base: 'px-4 py-3.5 text-sm text-muted whitespace-nowrap font-medium [&:has([role=checkbox])]:pe-0',
    pinned: 'sticky bg-default/75 data-[pinned=left]:left-0 data-[pinned=right]:right-0',
  },
  empty: 'py-6 text-center text-sm text-muted',
  loading: {
    base: 'py-6 text-center',
  },
  sticky: {
    thead: 'sticky top-0 inset-x-0 bg-default/75 z-[1] bg-white',
  },
}
</script>

<script setup lang="ts" generic="T extends TableData">
import { FlexRender, getCoreRowModel, getExpandedRowModel, getFilteredRowModel, getSortedRowModel, useVueTable } from '@tanstack/vue-table'
import { reactiveOmit } from '@vueuse/core'
import { Primitive } from 'reka-ui'
import { upperFirst } from 'scule'
import { HoveringFeature } from 'tanstack-table-hovering'
import { Comment, computed, h, ref, Text, watch } from 'vue'
import icons from '../styles/icons'
import AwButton from './AwButton.vue'
import AwCheckbox from './AwCheckbox.vue'
import AwIcon from './AwIcon.vue'
import AwTooltip from './AwTooltip.vue'

const props = withDefaults(defineProps<TableProps<T>>(), {
  watchOptions: () => ({
    deep: true,
  }),
})
const slots = defineSlots()

const data = ref(props.data ?? []) as Ref<T[]>
const columns = computed<TableColumn<T>[]>(() => {
  const cols = props.columns ?? []
  if (props.isSelectable && cols[0].id !== 'select') {
    cols.unshift({
      id: 'select',
      maxSize: 25,
      header: props.hideSelectAllCheckbox
        ? undefined
        : ({ table }) =>
            h(AwCheckbox, {
              'modelValue': table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : table.getIsAllPageRowsSelected(),
              'onUpdate:modelValue': (value: boolean | 'indeterminate') =>
                table.toggleAllPageRowsSelected(!!value),
              'aria-label': 'Select all',
            }),
      cell: ({ row }) =>
        h(AwCheckbox, {
          'modelValue': row.getIsSelected(),
          'onUpdate:modelValue': (value: boolean | 'indeterminate') => row.toggleSelected(!!value),
          'aria-label': 'Select row',
        }),
    })
  }
  if ((slots.actions || slots.primaryAction) && !cols.some(col => col.id === 'actions' || ('accessorKey' in col && col.accessorKey === 'actions'))) {
    cols.push({
      accessorKey: 'actions',
      header: '',
    })
  }
  return cols ?? Object.keys(data.value[0] ?? {}).map((accessorKey: string) => ({ accessorKey, header: upperFirst(accessorKey) }))
})
const meta = computed(() => props.meta ?? {})

const globalFilterState = defineModel<string>('globalFilter', { default: undefined })
const columnFiltersState = defineModel<ColumnFiltersState>('columnFilters', { default: [] })
const columnOrderState = defineModel<ColumnOrderState>('columnOrder', { default: [] })
const columnVisibilityState = defineModel<VisibilityState>('columnVisibility', { default: {} })
const columnPinningState = defineModel<ColumnPinningState>('columnPinning', { default: {} })
const columnSizingState = defineModel<ColumnSizingState>('columnSizing', { default: {} })
const columnSizingInfoState = defineModel<ColumnSizingInfoState>('columnSizingInfo', { default: {} })
const rowSelectionState = defineModel<RowSelectionState>('rowSelection', { default: {} })
const rowPinningState = defineModel<RowPinningState>('rowPinning', { default: {} })
const sortingState = defineModel<SortingState>('sorting', { default: [] })
const groupingState = defineModel<GroupingState>('grouping', { default: [] })
const expandedState = defineModel<ExpandedState>('expanded', { default: {} })
const paginationState = defineModel<PaginationState>('pagination', { default: {} })

const tableRef = ref<HTMLTableElement>()

const tableApi = useVueTable({
  ...reactiveOmit(props, 'as', 'data', 'columns', 'caption', 'sticky', 'loading', 'class'),
  data,
  columns: columns.value,
  meta: meta.value,
  getCoreRowModel: getCoreRowModel(),
  ...(props.globalFilterOptions || {}),
  onGlobalFilterChange: updaterOrValue => valueUpdater(updaterOrValue, globalFilterState),
  ...(props.columnFiltersOptions || {}),
  getFilteredRowModel: getFilteredRowModel(),
  onColumnFiltersChange: updaterOrValue => valueUpdater(updaterOrValue, columnFiltersState),
  onColumnOrderChange: updaterOrValue => valueUpdater(updaterOrValue, columnOrderState),
  ...(props.visibilityOptions || {}),
  onColumnVisibilityChange: updaterOrValue => valueUpdater(updaterOrValue, columnVisibilityState),
  ...(props.columnPinningOptions || {}),
  onColumnPinningChange: updaterOrValue => valueUpdater(updaterOrValue, columnPinningState),
  ...(props.columnSizingOptions || {}),
  onColumnSizingChange: updaterOrValue => valueUpdater(updaterOrValue, columnSizingState),
  onColumnSizingInfoChange: updaterOrValue => valueUpdater(updaterOrValue, columnSizingInfoState),
  ...(props.rowSelectionOptions || {}),
  onRowSelectionChange: updaterOrValue => valueUpdater(updaterOrValue, rowSelectionState),
  ...(props.rowPinningOptions || {}),
  onRowPinningChange: updaterOrValue => valueUpdater(updaterOrValue, rowPinningState),
  ...(props.sortingOptions || {}),
  getSortedRowModel: getSortedRowModel(),
  onSortingChange: updaterOrValue => valueUpdater(updaterOrValue, sortingState),
  ...(props.groupingOptions || {}),
  onGroupingChange: updaterOrValue => valueUpdater(updaterOrValue, groupingState),
  ...(props.expandedOptions || {}),
  getExpandedRowModel: getExpandedRowModel(),
  onExpandedChange: updaterOrValue => valueUpdater(updaterOrValue, expandedState),
  ...(props.paginationOptions || {}),
  onPaginationChange: updaterOrValue => valueUpdater(updaterOrValue, paginationState),
  ...(props.facetedOptions || {}),
  _features: [HoveringFeature],
  state: {
    get globalFilter() {
      return globalFilterState.value
    },
    get columnFilters() {
      return columnFiltersState.value
    },
    get columnOrder() {
      return columnOrderState.value
    },
    get columnVisibility() {
      return columnVisibilityState.value
    },
    get columnPinning() {
      return columnPinningState.value
    },
    get expanded() {
      return expandedState.value
    },
    get rowSelection() {
      return rowSelectionState.value
    },
    get sorting() {
      return sortingState.value
    },
    get grouping() {
      return groupingState.value
    },
    get rowPinning() {
      return rowPinningState.value
    },
    get columnSizing() {
      return columnSizingState.value
    },
    get columnSizingInfo() {
      return columnSizingInfoState.value
    },
    get pagination() {
      return paginationState.value
    },
  },
})

function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value = typeof updaterOrValue === 'function' ? updaterOrValue(ref.value) : updaterOrValue
}

function handleRowSelect(row: TableRow<T>, e: Event) {
  if (!props.onSelect) {
    return
  }
  const target = e.target as HTMLElement
  const isInteractive = target.closest('button') || target.closest('a')
  if (isInteractive) {
    return
  }
  e.preventDefault()
  e.stopPropagation()
  props.onSelect(row, e)
}

function hasActionsContent(cell: Cell<T, unknown>) {
  if (!slots.actions)
    return false
  const slotContent = slots.actions(cell.getContext())
  if (slotContent && Array.isArray(slotContent)) {
    return slotContent.some((vnode: any) => {
      if (!vnode || vnode.type === Comment)
        return false
      if (vnode.type !== Text)
        return true
      return vnode.children && vnode.children.toString().trim() !== ''
    })
  }
  return !!slotContent
}

function getHeaderLabelText(columnDef: ColumnDef<T, unknown>): string {
  const config = columnDef as TableColumn<T>
  if (config.hideHeaderText) {
    return ''
  }
  return typeof config.header === 'string' ? config.header : ''
}

function getLabelIcon(columnDef: ColumnDef<T, unknown>): string | undefined {
  return (columnDef as TableColumn<T>).labelIcon
}

function shouldHideHeaderText(columnDef: ColumnDef<T, unknown>): boolean {
  return !!(columnDef as TableColumn<T>).hideHeaderText
}

function getTooltip(columnDef: ColumnDef<T, unknown>): string | undefined {
  const tooltip = (columnDef as TableColumn<T>).tooltip
  return tooltip && tooltip.trim() !== '' ? tooltip : undefined
}

function getTdClass(cell: Cell<T, unknown>): string | undefined {
  const customClass = cell.column.columnDef.meta?.class?.td
  if (!customClass)
    return undefined
  return typeof customClass === 'string' ? customClass : customClass(cell)
}

watch(
  () => props.data,
  () => {
    data.value = props.data ? [...props.data] : []
  },
  props.watchOptions,
)

defineExpose({
  tableRef,
  tableApi,
})
</script>

<template>
  <Primitive :as="as" :class="[ui.root, props.class]">
    <table ref="tableRef" :class="ui.base">
      <caption v-if="caption || !!slots.caption" :class="ui.caption">
        <slot name="caption">
          {{ caption }}
        </slot>
      </caption>

      <thead :class="[ui.thead, props.sticky && ui.sticky.thead]">
        <tr v-for="headerGroup in tableApi.getHeaderGroups()" :key="headerGroup.id" :class="ui.tr">
          <th
            v-for="header in headerGroup.headers"
            :key="header.id"
            :data-pinned="header.column.getIsPinned()"
            :colspan="header.colSpan > 1 ? header.colSpan : undefined"
            :class="[ui.th.base, !!header.column.getIsPinned() && ui.th.pinned]"
            :style="{ width: `${header.getSize()}px`, maxWidth: `${header.getSize()}px` }"
          >
            <slot :name="`${header.id}-header`" v-bind="header.getContext()">
              <AwTooltip
                :disabled="!getTooltip(header.column.columnDef)"
                :text="getTooltip(header.column.columnDef)"
              >
                <template #default>
                  <AwButton
                    v-if="'isSortable' in header.column.columnDef && !!header.column.columnDef.isSortable"
                    color="gray" variant="link"
                    :label="getHeaderLabelText(header.column.columnDef)"
                    :leading-icon="getLabelIcon(header.column.columnDef)"
                    :trailing-icon="header.column.getIsSorted()
                      ? header.column.getIsSorted() === 'asc'
                        ? icons.arrowUpWide
                        : icons.arrowDownWide
                      : icons.arrowUpDown"
                    @click="header.column.toggleSorting(header.column.getIsSorted() === 'asc')"
                  />
                  <div
                    v-else-if="!header.isPlaceholder"
                    class="flex items-center gap-2"
                    :class="{ 'justify-center': shouldHideHeaderText(header.column.columnDef) && getLabelIcon(header.column.columnDef) }"
                  >
                    <AwIcon
                      v-if="getLabelIcon(header.column.columnDef)"
                      :name="getLabelIcon(header.column.columnDef)!"
                      style="min-width: 14px; min-height: 14px;"
                    />
                    <FlexRender
                      v-if="!shouldHideHeaderText(header.column.columnDef)"
                      :render="header.column.columnDef.header"
                      :props="header.getContext()"
                    />
                  </div>
                </template>
              </AwTooltip>
            </slot>
          </th>
        </tr>
      </thead>

      <tbody :class="ui.tbody">
        <template v-if="tableApi.getRowModel().rows?.length">
          <template v-for="row in tableApi.getRowModel().rows" :key="row.id">
            <tr
              :data-selected="row.getIsSelected()"
              :data-selectable="!!props.onSelect && row.getCanSelect()"
              :data-disabled="!row.getCanSelect()"
              :data-expanded="row.getIsExpanded()"
              :role="props.onSelect ? 'button' : undefined"
              :tabindex="props.onSelect ? 0 : undefined"
              :class="ui.tr"
              @mouseenter="!row.getIsHovered() && row.toggleHovered()"
              @mouseleave="row.getIsHovered() && row.toggleHovered()"
              @click="handleRowSelect(row, $event)"
            >
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :data-pinned="cell.column.getIsPinned()"
                :data-column-id="cell.column.id"
                :class="getTdClass(cell) || [
                  ui.td.base,
                  !!cell.column.getIsPinned() && ui.td.pinned,
                ]"
                :style="{ width: `${cell.column.getSize()}px`, maxWidth: `${cell.column.getSize()}px` }"
              >
                <slot :name="`${cell.column.id}-cell`" v-bind="cell.getContext()">
                  <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                </slot>
                <template v-if="cell.column.id === 'actions' && (slots.primaryAction || slots.actions)">
                  <div class="flex justify-end">
                    <div class="relative">
                      <div
                        v-if="slots.primaryAction && row.getIsHovered()"
                        class="absolute top-50% -translate-y-1/2 right-full pl-4 from-gray-50 via-gray-50 via-90% to-transparent bg-gradient-to-l"
                        :class="{ 'mr-1.5': hasActionsContent(cell) }"
                      >
                        <slot name="primaryAction" v-bind="cell.getContext()" />
                      </div>
                      <div v-if="slots.actions">
                        <slot name="actions" v-bind="cell.getContext()" />
                      </div>
                    </div>
                  </div>
                </template>
              </td>
            </tr>
            <tr v-if="row.getIsExpanded()" :class="ui.tr">
              <td :colspan="row.getAllCells().length" :class="ui.td.base">
                <slot name="expanded" :row="row" />
              </td>
            </tr>
          </template>
        </template>

        <tr v-else-if="loading && !!slots.loading">
          <td :colspan="columns?.length" :class="ui.loading.base">
            <slot name="loading" />
          </td>
        </tr>

        <tr v-else>
          <td :colspan="columns?.length" :class="ui.empty">
            <slot name="empty">
              {{ empty || 'Aucune donnée' }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </Primitive>
</template>

<style scoped>
tbody tr {
  @apply hover:bg-gray-50
}
</style>
